<script setup>
import { computed, onMounted } from 'vue'
import { useStore } from 'vuex'

import Badge from '@/components/Badge.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import ProjectDot from '@/components/project/ProjectDot.vue'

const props = defineProps({
	used: {
		type: Number,
		required: true,
	},
	label: {
		type: String,
		required: true,
	},
	limit: {
		type: Number,
		default: null,
	},
	// Tooltip copy: "Using X of Y {planLabel} on your plan." Defaults to `label`.
	planLabel: {
		type: String,
		default: '',
	},
	// When set, count-only mode uses singular for `used === 1` (e.g. "1 player").
	labelSingular: {
		type: String,
		default: '',
	},
	// Key into the account-wide counts (`live`, `vod`, `scheduled`, …). Set it
	// on pages whose list is scoped to one workspace: `used` then describes the
	// workspace and the plan figure comes from the account. Resources that are
	// not zoned leave it unset, because their list already is the account's.
	resource: {
		type: String,
		default: '',
	},
	// Preformatted quantities for non-integer usage (hours, bytes): shown in
	// place of `used` / `limit`, which still drive `hasLimit` and the tooltip.
	usedLabel: {
		type: String,
		default: '',
	},
	limitLabel: {
		type: String,
		default: '',
	},
	// Trailing copy after "used" on desktop, e.g. "· resets Oct 14".
	suffix: {
		type: String,
		default: '',
	},
	// 0..1: renders a small fill bar inside the chip (gold from 80%, red when full).
	percent: {
		type: Number,
		default: null,
	},
	// Replaces the default "Using X of Y … on your plan." tooltip sentence.
	tooltip: {
		type: String,
		default: '',
	},
})

const store = useStore()

const hasLimit = computed(() => props.limit !== null && props.limit !== undefined && props.limit !== '')

const countLabel = computed(() => (
	props.labelSingular && props.used === 1 ? props.labelSingular : props.label
))

const planResourceLabel = computed(() => props.planLabel || props.label)

// What the account holds against what this workspace holds, or null while that
// is unknown — in which case the meter says only what it can vouch for.
const counts = computed(() => (
	props.resource ? store.getters['Projects/resourceCounts'](props.resource) : null
))

// The figure to weigh against the allowance is always the account's: the plan
// is bought once and spent everywhere.
//
// The page's list is live while the server's counts are a snapshot, so anything
// created or deleted here since moves both totals by the same amount. Applying
// that difference keeps the meter honest without re-asking the server on every
// change.
const planUsed = computed(() => {
	if (!counts.value) return props.used
	return Math.max(counts.value.account + (props.used - counts.value.workspace), 0)
})

// Splitting the count is only worth the words once the account actually has
// somewhere else to put things. With a single workspace the two numbers are the
// same and naming the workspace is noise.
const hasMultipleProjects = computed(() => store.getters['Projects/hasMultipleProjects'])
const showSplit = computed(() => hasLimit.value && counts.value !== null && hasMultipleProjects.value)

const activeProject = computed(() => store.getters['Projects/activeProject'])
const projectColor = computed(() => activeProject.value?.color || '#3b82f6')

onMounted(() => {
	if (props.resource) store.dispatch('Projects/loadResourceUsage')
})

// The plan figure, not the page's: on a zoned resource `planUsed` is the
// account's and `used` is only this workspace's share. They are the same number
// wherever `resource` is unset, so the chip below reads correctly either way.
const usedText = computed(() => props.usedLabel || String(planUsed.value))
const limitText = computed(() => props.limitLabel || String(props.limit))

// One string so an empty `label` never leaves a double space ("10h  used").
const fullText = computed(() => (
	[usedText.value, 'of', limitText.value, props.label, 'used', props.suffix].filter(Boolean).join(' ')
))

const hasBar = computed(() => Number.isFinite(props.percent) && props.percent !== null)
const barWidth = computed(() => `${Math.round(Math.min(1, Math.max(0, props.percent)) * 100)}%`)
const barClass = computed(() => {
	if (props.percent >= 1) return 'bg-error'
	if (props.percent >= 0.8) return 'bg-gold'
	return 'bg-main'
})
</script>

<template>
	<!-- Zoned resource, more than one workspace: what is here, and what the plan
	     is carrying everywhere. Without both, a page showing one stream of a
	     four-stream plan looks like three are free when they are not. -->
	<Tooltip v-if="showSplit" :hide="600">
		<span class="inline-flex items-center rounded-full ring-1 ring-surface-4 text-xs font-medium leading-none" data-testid="header-usage">
			<span class="inline-flex items-center gap-1.5 px-2 py-1 text-surface-9">
				<ProjectDot :color="projectColor" size="size-1.5" />
				<span class="md:hidden">{{ used }}</span>
				<span class="hidden md:inline">{{ used }} in this workspace</span>
			</span>
			<span class="h-3 w-px bg-surface-4" aria-hidden="true" />
			<span class="px-2 py-1 text-surface-7">
				<span class="md:hidden">{{ planUsed }} / {{ limit }}</span>
				<span class="hidden md:inline">{{ planUsed }} of {{ limit }} used on your plan</span>
			</span>
		</span>
		<template #content>
			<span class="text-dark-8 text-small">
				{{ used }} of the {{ planUsed }} {{ planResourceLabel }} on your plan
				{{ used === 1 ? 'is' : 'are' }} in this workspace.
				The {{ limit }} your plan allows are shared across all of them.
				Need more? <a class="underline text-white" target="_blank" href="/app/subscribe">Upgrade here</a>
			</span>
		</template>
	</Tooltip>

	<!-- Everywhere else: one figure against the allowance. `usedText` already
	     resolves to the account's, so this reads right on a zoned resource
	     viewed from an account that has only the one workspace. -->
	<Tooltip v-else-if="hasLimit" :hide="600">
		<Badge type="main" inline pill size="sm" class="gap-1.5" data-testid="header-usage">
			<span v-if="hasBar" class="hidden md:inline-block h-1.5 w-14 overflow-hidden rounded-full bg-surface-6" aria-hidden="true">
				<span class="block h-full rounded-full" :class="barClass" :style="{ width: barWidth }" />
			</span>
			<span v-else class="hidden md:inline-block size-1.5 rounded-full bg-surface-6" />
			<span class="md:hidden">{{ usedText }} / {{ limitText }}</span>
			<span class="hidden md:inline">{{ fullText }}</span>
		</Badge>
		<template #content>
			<span class="text-dark-8 text-small">
				<template v-if="tooltip">{{ tooltip }}</template>
				<template v-else>Using {{ usedText }} of {{ limitText }} {{ planResourceLabel }} on your plan.</template>
				Need more? <a class="underline text-white" target="_blank" href="/app/subscribe">Upgrade here</a>
			</span>
		</template>
	</Tooltip>

	<Badge v-else type="main" inline pill size="sm" class="gap-1.5" data-testid="header-usage">
		<span class="hidden md:inline-block size-1.5 rounded-full bg-surface-6" />
		<span class="md:hidden">{{ usedText }}</span>
		<span class="hidden md:inline">{{ usedText }} {{ countLabel }}</span>
	</Badge>
</template>
